import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { countCharacters, projectCharacters } from '@/lib/cast-counts';

// Mensaje claro cuando un guardado de presupuesto falla. Antes estos mutations
// no tenían onError → un fallo (p. ej. sesión caducada → RLS) revertía la celda
// en silencio ("edito y revierte"). Ahora el usuario ve la causa.
function budgetWriteError(e: unknown) {
  const msg = (e as Error)?.message || 'error desconocido';
  toast.error(`No se pudo guardar el cambio: ${msg}. Si dice "row-level security" o similar, refresca la página (sesión caducada) y reintenta.`);
}
import {
  startAnalysis, cancelAnalysis, continueAnalysis,
  fetchScenes, fetchCharacters, fetchAggregatedCast, fetchLocations,
  fetchNarrativeAnalysis, fetchCharacterArcs, fetchEmotionalMap,
  fetchCastingProfiles, updateCastingProfile, fetchBudgetEstimate, fetchDistributionStrategy,
  fetchThemes, fetchDiagnostics, fetchConflicts,
  fetchAnalysisJobs, fetchAnalysisJob, updateAnalysisJob, deleteAnalysisJob,
  fetchBudgetLines, createBudgetLine, updateBudgetLine, deleteBudgetLine, seedBudgetFromTariffs,
  undoLastBudgetSeed, hasBudgetSnapshot,
  fetchBudgetBrief, saveBudgetBrief,
  fetchStageStatuses, fetchStageSignals, updateStageStatus, initializeStages,
  fetchProjectContent, upsertProjectContent, mergeLocationRegion,
  fetchChatMessages, sendChatMessage, applyChatActions, generateContent,
  fetchBudgetParams, upsertBudgetParam, fetchBudgetCoproducers, addBudgetCoproducer, removeBudgetCoproducer,
  updateScene, saveScriptSnapshot, fetchScriptSnapshots,
  updateLocationGeo,
  updateLocation, createLocation, deleteLocation, mergeLocations, assignScenesToLocation,
  fetchBudgetScenarios, saveBudgetScenario, deleteBudgetScenario, promoteBudgetScenario,
  deleteScene,
} from '@/services/scriptAnalysisService';
import { runBudgetV2, type RunBudgetV2Options } from '@/services/budgetV2Service';
import type { StartAnalysisOptions } from '@/services/scriptAnalysisService';
import type { AnalysisSummary } from '@/types/scriptAnalysis';

// OJO al reconstruir las opciones a mano: este hook es un tramo INTERMEDIO entre el
// selector de subida y el servicio, y cualquier campo que no se reenvíe aquí se pierde en
// silencio. Pasó con `numCapitulos`: el usuario escribía 12 capítulos y toda serie se
// registraba con el valor por defecto de 8, o sea un 33% de error en el presupuesto de la
// temporada sin que nada avisara. Por eso ahora se propaga el objeto entero en vez de
// enumerar campos, y el tipo viene del propio servicio para que el compilador cace el
// siguiente que se añada.
export function useStartAnalysis() {
  return useMutation({
    mutationFn: (input: { file: File } & StartAnalysisOptions) => {
      const { file, ...opciones } = input;
      return startAnalysis(file, opciones);
    },
  });
}

export function useCancelAnalysis() {
  return useMutation({
    mutationFn: (jobId: string) => cancelAnalysis(jobId),
  });
}

export function useContinueAnalysis() {
  return useMutation({
    mutationFn: (jobId: string) => continueAnalysis(jobId),
  });
}

// Estados en curso: mientras haya alguno, hacemos polling vivo (para ver el
// progreso y para que el watchdog detecte a tiempo un job sin latido).
const IN_FLIGHT = new Set(['processing', 'needs_continue']);
function hasInFlight(jobs: Array<{ status?: string } | null | undefined> | undefined | null): boolean {
  return !!jobs?.some((j) => !!j && IN_FLIGHT.has(String(j.status)));
}
function jobInFlight(job: { status?: string } | null | undefined): boolean {
  return !!job && IN_FLIGHT.has(String(job.status));
}

export function useAnalysisJobs() {
  // Gate por sesión: si la query arranca antes de que Supabase hidrate el token,
  // va como anonima -> RLS devuelve 0 -> lista vacia y "hay que refrescar".
  // Con enabled esperamos a que la sesion este lista (clave en Safari, mas lento).
  const { session, loading } = useAuth();
  return useQuery({
    queryKey: ['analysis-jobs'],
    queryFn: () => fetchAnalysisJobs(),
    enabled: !loading && !!session,
    // Polling solo cuando hay trabajo en curso (8s); si no, sin polling.
    refetchInterval: (q) => (hasInFlight(q.state.data as Array<{ status?: string }>) ? 8_000 : false),
  });
}

export function useAnalysisJob(jobId: string | null) {
  return useQuery({
    queryKey: ['analysis-job', jobId],
    queryFn: () => fetchAnalysisJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => (jobInFlight(q.state.data as { status?: string } | undefined) ? 8_000 : false),
  });
}

// ── Watchdog de auto-recuperación ────────────────────────────────────────────
// Un análisis no puede quedarse trabado. Si una invocación del edge muere a
// mitad (timeout/API 529) antes de relanzar su self-invoke, el job queda
// huérfano (`processing` o `needs_continue`) SIN latido. Este watchdog (cliente)
// lo detecta y dispara `continueAnalysis`, que reanuda desde las escenas ya
// guardadas, saltando la fase muerta.
//
// Es SOLO red de seguridad: actúa únicamente cuando NO hay latido (>75s sin
// `updated_at` nuevo). Mientras el self-invoke del edge funciona, el job
// heartbeatea y el watchdog no toca nada → no compite con el self-invoke sano
// (evita doble-continue). El backend corta solo a los 40 reintentos poniendo
// status=failed (fuera de IN_FLIGHT), así que el watchdog tampoco bucle-infinita.
// 130s: por encima del wall-clock de una invocación sana (110s) → no dispara a
// mitad de una fase larga legítima, pero rescata enseguida un job realmente
// muerto (que se queda congelado minutos).
const STALE_MS = 130_000;
const RETRY_THROTTLE_MS = 130_000; // no reintentar el mismo job más de 1 vez/130s

export function useAnalysisAutoRecover(
  jobs: Array<{ id: string; status?: string; updated_at?: string; stage?: string; total_scenes?: number | null }> | undefined,
) {
  const lastTry = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!jobs?.length) return;
    const now = Date.now();
    for (const j of jobs) {
      if (!IN_FLIGHT.has(String(j.status))) continue;

      // `continue` reanuda desde las escenas guardadas: si aún no hay escenas
      // (murió en validación inicial), no es recuperable así → no spameamos.
      if (!(Number(j.total_scenes) > 0)) continue;

      // Solo rescatamos si perdió el latido (el self-invoke sano heartbeatea).
      const updatedMs = j.updated_at ? new Date(j.updated_at).getTime() : 0;
      if (now - updatedMs <= STALE_MS) continue;

      const last = lastTry.current[j.id] ?? 0;
      if (now - last < RETRY_THROTTLE_MS) continue;
      lastTry.current[j.id] = now;

      console.warn(`[auto-recover] job ${j.id} sin latido (status=${j.status}, stage=${j.stage ?? '?'}) → reanudando`);
      continueAnalysis(j.id).catch((e) => {
        console.error(`[auto-recover] falló reanudar ${j.id}:`, e);
      });
    }
  }, [jobs]);
}

export function useUpdateAnalysisJob() {
  return useMutation({
    mutationFn: ({ jobId, updates }: { jobId: string; updates: Parameters<typeof updateAnalysisJob>[1] }) =>
      updateAnalysisJob(jobId, updates),
  });
}

export function useDeleteAnalysisJob() {
  return useMutation({
    mutationFn: (jobId: string) => deleteAnalysisJob(jobId),
  });
}

export function useScenes(jobId: string | null) {
  return useQuery({
    queryKey: ['scenes', jobId],
    queryFn: () => fetchScenes(jobId!),
    enabled: !!jobId,
  });
}

export function useCharacters(jobId: string | null) {
  return useQuery({
    queryKey: ['characters', jobId],
    queryFn: () => fetchCharacters(jobId!),
    enabled: !!jobId,
  });
}

export function useAggregatedCast(jobId: string | null) {
  return useQuery({
    queryKey: ['aggregated-cast', jobId],
    queryFn: () => fetchAggregatedCast(jobId!),
    enabled: !!jobId,
  });
}

export function useLocations(jobId: string | null) {
  return useQuery({
    queryKey: ['locations', jobId],
    queryFn: () => fetchLocations(jobId!),
    enabled: !!jobId,
  });
}

export function useNarrativeAnalysis(jobId: string | null) {
  return useQuery({ queryKey: ['narrative', jobId], queryFn: () => fetchNarrativeAnalysis(jobId!), enabled: !!jobId });
}
export function useCharacterArcs(jobId: string | null) {
  return useQuery({ queryKey: ['arcs', jobId], queryFn: () => fetchCharacterArcs(jobId!), enabled: !!jobId });
}
export function useEmotionalMap(jobId: string | null) {
  return useQuery({ queryKey: ['emotions', jobId], queryFn: () => fetchEmotionalMap(jobId!), enabled: !!jobId });
}
export function useCastingProfiles(jobId: string | null) {
  return useQuery({ queryKey: ['casting', jobId], queryFn: () => fetchCastingProfiles(jobId!), enabled: !!jobId });
}
export function useUpdateCastingProfile() {
  return useMutation({
    // `origen` sin declarar significa 'persona', que es lo que hace el diálogo de la ficha:
    // así el guardado del productor marca los campos que ha tocado y el re-análisis los
    // respeta. Los procesos automáticos (el auto-sync de días de CastingPage) tienen que
    // pasar 'proceso' de forma explícita, o marcarían como humano algo que no lo es.
    mutationFn: ({ id, updates, origen }: {
      id: string;
      updates: Parameters<typeof updateCastingProfile>[1];
      origen?: Parameters<typeof updateCastingProfile>[2];
    }) => updateCastingProfile(id, updates, origen),
  });
}
export function useBudgetEstimate(jobId: string | null) {
  return useQuery({ queryKey: ['budget', jobId], queryFn: () => fetchBudgetEstimate(jobId!), enabled: !!jobId });
}
// Dispara el swarm budget-v2 (agent-runtime · Node). Síncrono · ~8-10 min.
// Al terminar invalida ['budget', jobId] para refrescar lines_v2/budget_v2_at.
export function useRunBudgetV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, ...opts }: { jobId: string } & RunBudgetV2Options) =>
      runBudgetV2(jobId, opts),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['budget', vars.jobId] });
    },
  });
}
export function useDistributionStrategy(jobId: string | null) {
  return useQuery({ queryKey: ['distribution', jobId], queryFn: () => fetchDistributionStrategy(jobId!), enabled: !!jobId });
}
export function useThemes(jobId: string | null) {
  return useQuery({ queryKey: ['themes', jobId], queryFn: () => fetchThemes(jobId!), enabled: !!jobId });
}
export function useDiagnostics(jobId: string | null) {
  return useQuery({ queryKey: ['diagnostics', jobId], queryFn: () => fetchDiagnostics(jobId!), enabled: !!jobId });
}
// === Budget Lines hooks ===
export function useBudgetLines(jobId: string | null) {
  return useQuery({
    queryKey: ['budget-lines', jobId],
    queryFn: () => fetchBudgetLines(jobId!),
    enabled: !!jobId,
  });
}

export function useCreateBudgetLine() {
  return useMutation({ mutationFn: createBudgetLine, onError: budgetWriteError });
}

export function useUpdateBudgetLine() {
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof updateBudgetLine>[1] }) =>
      updateBudgetLine(id, updates),
    onSuccess: (_d, { id, updates }) => {
      // Fase 2 (perfil del productor): NO guardamos la tarifa como preferencia sola —
      // puede ser solo de ESTE proyecto. Preguntamos; solo si el productor confirma, se
      // recuerda como su tarifa habitual (y desde ahí los expertos la usan).
      const u = updates as Record<string, unknown> | undefined;
      if (u && ('precio_unitario' in u || 'unit_price' in u)) {
        toast('¿Recordar esta tarifa como tu tarifa habitual?', {
          description: 'Tus expertos la usarán en futuros presupuestos. Si es solo de este proyecto, ignorá esto.',
          action: {
            label: 'Recordar',
            onClick: () => {
              supabase.functions.invoke('learn-budget-edit', { body: { line_id: id } })
                .then(() => toast.success('Aprendido como tu tarifa habitual'))
                .catch(() => toast.error('No se pudo guardar la preferencia'));
            },
          },
        });
      }
    },
    onError: budgetWriteError,
  });
}

export function useDeleteBudgetLine() {
  return useMutation({ mutationFn: deleteBudgetLine, onError: budgetWriteError });
}

export function useSeedBudget() {
  return useMutation({
    mutationFn: ({ jobId, tariffs }: { jobId: string; tariffs: Parameters<typeof seedBudgetFromTariffs>[1] }) =>
      seedBudgetFromTariffs(jobId, tariffs),
  });
}

export function useUndoBudgetSeed() {
  return useMutation({ mutationFn: (jobId: string) => undoLastBudgetSeed(jobId) });
}

export function useBudgetBrief(jobId: string | null) {
  return useQuery({
    queryKey: ['budget-brief', jobId],
    queryFn: () => fetchBudgetBrief(jobId!),
    enabled: !!jobId,
  });
}

export function useSaveBudgetBrief() {
  return useMutation({
    mutationFn: ({ jobId, brief }: { jobId: string; brief: Record<string, unknown> }) =>
      saveBudgetBrief(jobId, brief),
  });
}

export function useBudgetSnapshotExists(jobId: string | null) {
  return useQuery({
    queryKey: ['budget-snapshot-exists', jobId],
    queryFn: () => hasBudgetSnapshot(jobId!),
    enabled: !!jobId,
  });
}

export function useConflicts(jobId: string | null) {
  return useQuery({
    queryKey: ['conflicts', jobId],
    queryFn: () => fetchConflicts(jobId!),
    enabled: !!jobId,
  });
}

export function useAnalysisSummary(jobId: string | null): AnalysisSummary | null {
  const { data: scenes } = useScenes(jobId);
  const { data: cast } = useAggregatedCast(jobId);
  const { data: locations } = useLocations(jobId);
  if (!scenes || !cast || !locations) return null;
  return {
    total_scenes: scenes.length,
    total_characters: countCharacters(projectCharacters(cast, scenes)),
    total_locations: locations.length,
    total_pages: Math.round(scenes.reduce((sum, s) => sum + s.page_eighths, 0) / 8 * 10) / 10,
  };
}

// === Stage management hooks ===
export function useStageStatuses(jobId: string | null) {
  return useQuery({
    queryKey: ['stage-statuses', jobId],
    queryFn: () => fetchStageStatuses(jobId!),
    enabled: !!jobId,
  });
}

export function useStageSignals(jobId: string | null) {
  return useQuery({
    queryKey: ['stage-signals', jobId],
    queryFn: () => fetchStageSignals(jobId!),
    enabled: !!jobId,
  });
}

export function useUpdateStageStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, stage, status }: { jobId: string; stage: string; status: string }) =>
      updateStageStatus(jobId, stage, status),
    onSuccess: (_data, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['stage-statuses', jobId] });
    },
  });
}

export function useInitializeStages() {
  return useMutation({ mutationFn: initializeStages });
}

// === Project content hooks ===
export function useProjectContent(jobId: string | null) {
  return useQuery({
    queryKey: ['project-content', jobId],
    queryFn: () => fetchProjectContent(jobId!),
    enabled: !!jobId,
  });
}

export function useUpsertProjectContent() {
  return useMutation({
    mutationFn: ({ jobId, contentType, content, params }: { jobId: string; contentType: string; content: string; params?: Record<string, unknown> }) =>
      upsertProjectContent(jobId, contentType, content, params),
  });
}

// Merge atómico+serializado de una entrada del mapa location_regions (sin race).
export function useMergeLocationRegion() {
  return useMutation({
    mutationFn: ({ jobId, key, value }: { jobId: string; key: string; value: Record<string, unknown> }) =>
      mergeLocationRegion(jobId, key, value),
  });
}

export function useGenerateContent() {
  return useMutation({
    mutationFn: ({ jobId, contentType, instructions, destination, formAnswers, currentContent }: {
      jobId: string;
      contentType: string;
      instructions?: string;
      destination?: string;
      formAnswers?: Record<string, string | string[]>;
      currentContent?: string;
    }) => generateContent(jobId, contentType, instructions, destination, formAnswers, currentContent),
  });
}

// === Chat hooks ===
export function useChatMessages(jobId: string | null, stage: string) {
  return useQuery({
    queryKey: ['chat-messages', jobId, stage],
    queryFn: () => fetchChatMessages(jobId!, stage),
    enabled: !!jobId,
  });
}

export function useSendChatMessage() {
  return useMutation({
    mutationFn: ({ jobId, stage, content, userName }: { jobId: string; stage: string; content: string; userName?: string }) =>
      sendChatMessage(jobId, stage, content, userName),
  });
}

export function useApplyChatActions() {
  return useMutation({
    mutationFn: ({ messageId, actions }: { messageId: string; actions: Array<{ table: string; operation: string; id?: string; data?: Record<string, unknown> }> }) =>
      applyChatActions(messageId, actions),
  });
}

export function useBudgetParams(jobId: string | null) {
  return useQuery({
    queryKey: ['budget-params', jobId],
    queryFn: () => fetchBudgetParams(jobId!),
    enabled: !!jobId,
  });
}

export function useUpsertBudgetParam() {
  return useMutation({
    mutationFn: ({ jobId, key, value }: { jobId: string; key: string; value: number }) =>
      upsertBudgetParam(jobId, key, value),
  });
}

export function useBudgetCoproducers(jobId: string | null) {
  return useQuery({
    queryKey: ['budget-coproducers', jobId],
    queryFn: () => fetchBudgetCoproducers(jobId!),
    enabled: !!jobId,
  });
}

export function useAddBudgetCoproducer() {
  return useMutation({
    mutationFn: ({ jobId, code, name, order }: { jobId: string; code: string; name: string; order: number }) =>
      addBudgetCoproducer(jobId, code, name, order),
  });
}

export function useRemoveBudgetCoproducer() {
  return useMutation({
    mutationFn: (id: string) => removeBudgetCoproducer(id),
  });
}

export function useUpdateLocationGeo() {
  return useMutation({
    mutationFn: ({ locationId, geo }: { locationId: string; geo: Parameters<typeof updateLocationGeo>[1] }) =>
      updateLocationGeo(locationId, geo),
  });
}

// === Location · canonical CRUD hooks (Localizador stage) ===

export function useUpdateLocation(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, patch }: { locationId: string; patch: Record<string, unknown> }) =>
      updateLocation(locationId, patch),
    onSuccess: () => {
      if (jobId) queryClient.invalidateQueries({ queryKey: ['locations', jobId] });
    },
  });
}

export function useCreateLocation(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: Parameters<typeof createLocation>[1] }) =>
      createLocation(jobId!, data),
    onSuccess: () => {
      if (jobId) queryClient.invalidateQueries({ queryKey: ['locations', jobId] });
    },
  });
}

export function useDeleteLocation(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => deleteLocation(locationId),
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ['locations', jobId] });
        queryClient.invalidateQueries({ queryKey: ['scenes', jobId] });
      }
    },
  });
}

export function useMergeLocations(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, sourceIds }: { targetId: string; sourceIds: string[] }) =>
      mergeLocations(targetId, sourceIds),
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ['locations', jobId] });
        queryClient.invalidateQueries({ queryKey: ['scenes', jobId] });
      }
    },
  });
}

export function useAssignScenesToLocation(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sceneIds, locationId }: { sceneIds: string[]; locationId: string | null }) =>
      assignScenesToLocation(sceneIds, locationId),
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: ['scenes', jobId] });
        queryClient.invalidateQueries({ queryKey: ['locations', jobId] });
      }
    },
  });
}

export function useUpdateScene(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sceneId, updates }: { sceneId: string; updates: Parameters<typeof updateScene>[1] }) =>
      updateScene(sceneId, updates),
    onSuccess: () => {
      if (jobId) queryClient.invalidateQueries({ queryKey: ['scenes', jobId] });
    },
  });
}

export function useDeleteScene(jobId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sceneId: string) => deleteScene(sceneId),
    onSuccess: () => {
      if (!jobId) return;
      // Al quitar una escena cambian el metraje, el plan y el desglose: se invalida todo lo
      // que cuelga de las escenas para que ninguna pantalla siga con la cuenta vieja.
      for (const key of [['scenes', jobId], ['locations', jobId], ['budget-params', jobId], ['casting', jobId], ['characters', jobId], ['aggregated-cast', jobId]]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      queryClient.invalidateQueries({ queryKey: ['analysis-job', jobId] });
      // La lista de proyectos muestra el número de escenas, que acaba de cambiar.
      queryClient.invalidateQueries({ queryKey: ['analysis-jobs'] });
    },
  });
}

export function useSaveScriptSnapshot() {
  return useMutation({
    mutationFn: ({ jobId, scenes }: { jobId: string; scenes: Parameters<typeof saveScriptSnapshot>[1] }) =>
      saveScriptSnapshot(jobId, scenes),
  });
}

export function useScriptSnapshots(jobId: string | null) {
  return useQuery({
    queryKey: ['script-snapshots', jobId],
    queryFn: () => fetchScriptSnapshots(jobId!),
    enabled: !!jobId,
  });
}

// === Budget Scenarios hooks ===

export function useBudgetScenarios(jobId: string | null) {
  return useQuery({ queryKey: ['budget-scenarios', jobId], queryFn: () => fetchBudgetScenarios(jobId!), enabled: !!jobId });
}
export function useSaveBudgetScenario() {
  return useMutation({ mutationFn: saveBudgetScenario });
}
export function useDeleteBudgetScenario() {
  return useMutation({ mutationFn: (id: string) => deleteBudgetScenario(id) });
}
export function usePromoteBudgetScenario() {
  return useMutation({
    mutationFn: ({ jobId, scenario }: { jobId: string; scenario: { id: string; shooting_days: number; lines: Record<string, unknown>[] } }) =>
      promoteBudgetScenario(jobId, scenario),
  });
}
